"use client";

import React from "react";
import useGetHeroImageByTag from "@/components/hooks/use-get-hero-image-by-tag";
import { Button } from "@/components/ui/button";
import BackgroundImage from "@/components/ui/layout/background-image";
import Container from "@/components/ui/layout/container";
import Flex from "@/components/ui/layout/flex";
import Section from "@/components/ui/layout/section";
import Spacer from "@/components/ui/layout/spacer";
import { MotionLink } from "@/components/ui/Motion";
import {
  TypographyH1,
  TypographyH2,
  TypographySmall,
} from "@/components/ui/typography";
import { URLS } from "@/lib/consts";
import type { FloorPlanName } from "../../../types";
import { FLOOR_PLAN_HERO_DESCRIPTIONS } from "../../consts";

interface FloorPlansNameHeroProperties {
  floorPlanName: FloorPlanName;
}

function FloorPlansNameHero({ floorPlanName }: FloorPlansNameHeroProperties) {
  const tag = `hero_floor_plans_${floorPlanName.replace(/\s+/g, '_')}`;
  const { data: heroImage } = useGetHeroImageByTag({ tag });

  const content = (
    <>
      <Spacer size="h-[50px]" />

      {/* Header */}
      <Flex direction="column" gap="gap-2">
        <Container maxWidth="max-w-2xl">
          <Flex
            direction="column"
            gap="gap-2 md:gap-4 lg:gap-6"
            className="text-center"
          >
            <TypographyH2 className="font-bold border-b-0">
              FLOOR PLAN
            </TypographyH2>

            <TypographyH1>{floorPlanName}</TypographyH1>

            <TypographySmall>
              {FLOOR_PLAN_HERO_DESCRIPTIONS[floorPlanName]}
            </TypographySmall>

            <Flex direction="column" md="md:flex-row" gap="gap-4">
              <Button size="lg" variant="secondary" asChild>
                <MotionLink href={URLS.SERVICES.DESIGN_AND_BUILD}>
                  Our Build Process
                </MotionLink>
              </Button>

              <Button size="lg" variant="secondary" asChild>
                <MotionLink href={URLS.SERVICES.DESIGN_AND_RENOVATE}>
                  Let&apos;s Talk Renovations
                </MotionLink>
              </Button>
            </Flex>
          </Flex>
        </Container>
      </Flex>
    </>
  );

  if (!heroImage) {
    return (
      <Section id="hero" className="bg-primary">
        <Container id="hero" maxWidth="max-w-5xl">
          {content}
        </Container>
      </Section>
    );
  }

  return (
    <Section id="hero" className="relative min-h-[80vh]">
      <BackgroundImage
        src={heroImage}
        alt={`${floorPlanName} Floor Plan`}
        fill
        sizes="100vw"
        overlay="bg-black/40"
      >
        <Container id="hero" maxWidth="max-w-5xl" className="text-white">
          {content}
        </Container>
      </BackgroundImage>
    </Section>
  );
}

export default FloorPlansNameHero;
